import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import { XAlertService } from '../x-alert.service';
import { EMPTY, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  LogisticRequestRatingActionEnum,
  LogisticRequestRatingList,
  RatingsStatus,
  RequestRatingsServiceTranslations,
  RequestRatingsServiceTranslationsPayload,
} from '@core/types';
import { catchError } from 'rxjs/operators';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class RequestRatingsService extends RequestEntitiesService<
  LogisticRequestRatingList[]
> {
  private xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly requestsApiService = inject(RequestsApiService);
  readonly $request = this.requestService.$request;
  private $selectedRequests = this.requestService.selectedRequests;
  override readonly destroyRef = inject(DestroyRef);
  public $allRatings = signal<LogisticRequestRatingList[]>([]);
  public $checkedRatings = signal<LogisticRequestRatingList[]>([]);
  public $ratingsStatus = signal<RatingsStatus>({});

  readonly source$ = (): Observable<LogisticRequestRatingList[]> => {
    return this.requestService.request$.pipe(
      map(
        request =>
          request.ratingsWithDetails?.filter(rating => rating.type === 1) || [],
      ),
    );
  };

  private translates: RequestRatingsServiceTranslationsPayload = {
    [RequestRatingsServiceTranslations.ErrorGettingRatings]:
      'Случилась ошибка при получении всех рейтингов!',
  };

  constructor() {
    super();
    this.outkeepTranslateService
      .translate([RequestRatingsServiceTranslations.ErrorGettingRatings])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  public requestRatingsForInstaller$(): Observable<
    LogisticRequestRatingList[]
  > {
    return this.requestsApiService.getRequestRatingsForInstaller$();
  }

  public toggleRating(
    ratingId: LogisticRequestRatingList['id'],
    action: LogisticRequestRatingActionEnum,
    groupAction = false,
  ): Observable<void> | Observable<void[]> {
    if (groupAction) {
      return forkJoin(
        this.$selectedRequests().map(({ id }) =>
          this.requestsApiService.toggleRequestRating$(id, ratingId, action),
        ),
      );
    }

    return this.requestService.requestId$.pipe(
      switchMap(requestId =>
        this.requestsApiService.toggleRequestRating$(
          requestId,
          ratingId,
          action,
        ),
      ),
    );
  }

  public setupRatings(groupAction = false) {
    if (this.$allRatings().length) {
      return this.mapRatingsToStatus(groupAction);
    }

    return this.requestRatingsForInstaller$().pipe(
      catchError(() => {
        this.xAlertService.error(
          this.translates[
            RequestRatingsServiceTranslations.ErrorGettingRatings
          ],
        );

        return EMPTY;
      }),
      switchMap(allRatings => {
        this.$allRatings.set(allRatings);

        return this.mapRatingsToStatus(groupAction);
      }),
    );
  }

  mapRatingsToStatus(groupAction = false) {
    const ratingsStatus: RatingsStatus = {};

    if (groupAction) {
      this.$allRatings().forEach(rating => {
        ratingsStatus[rating.id] = false;
      });
      this.$ratingsStatus.set(ratingsStatus);
      this.sortRatings(ratingsStatus);

      return of(null);
    }

    return this.entities$.pipe(
      map(currentReqRatings => {
        const currentReqRatingsMap = new Map(
          currentReqRatings.map(rating => [rating.id, rating]),
        );

        this.$allRatings().forEach(rating => {
          ratingsStatus[rating.id] = currentReqRatingsMap.has(rating.id);
        });

        this.$checkedRatings.set(
          this.$allRatings().filter(rating => ratingsStatus[rating.id]),
        );

        this.$ratingsStatus.set(ratingsStatus);
        this.sortRatings(ratingsStatus);
      }),
    );
  }

  private sortRatings(ratingsStatus: RatingsStatus) {
    this.$allRatings().sort((a, b) => {
      return (ratingsStatus[b.id] ? 1 : 0) - (ratingsStatus[a.id] ? 1 : 0);
    });
  }
}
